/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { useNavigate, useParams } from 'react-router-dom'
import { doc, collection, getDoc, updateDoc, DocumentSnapshot, onSnapshot } from 'firebase/firestore'
import { httpsCallable } from "firebase/functions"
import { db, functions } from '../../firebase'
import { Box, Typography, Table, TableBody, TableHead, TableRow, TableCell, TableContainer, Paper, Backdrop, CircularProgress, Stack, Fab } from "@mui/material"
import FormElement from "../lib/FormElement"
import isEmpty from 'lodash.isempty'
import { validate } from "../lib/FormValidation"
import parse from 'html-react-parser'
import { toast } from 'react-toastify'
import { printBarcode } from "./lib/PrintLabel"
import { PopImageView } from "../lib/PopImageView"
import IndividualStatusModal from "./lib/IndividualStatusModal"
import ProcessingRecordModal from "./lib/ProcessingRecordModal"
import { setIndividualStatus } from "./lib/common"
import dayjs from "dayjs"

const createIndividualNo = httpsCallable(functions, 'createIndividualNo')
const sendMail = httpsCallable(functions, 'sendMail')
const mailTemplate = (id: string) => {
return `
個体の預かり票を発行しました。
以下リンクよりご確認ください。

https://traceability.rindou.info/individuals/${id}/receipt
`
}

const IndividualDetails = () => {
  const urlParams = useParams<{ individualId: string }>()
  const navigate = useNavigate()
  const [forms, setForms] = useState<any>()
  const [formValues, setFormValues] = useState<any>()
  const [error, setError] = useState<string>('')
  const [prosessing, setProcessing] = useState<boolean>(false)
  const [openStatusModal, setOpenStatusModal] = useState<boolean>(false)
  const [openProcessingModal, setOpenProcessingModal] = useState<boolean>(false)

  useEffect(() => {
    getDoc(doc(collection(db, 'forms'), 'individual')).then((_: DocumentSnapshot) => {
      _.exists() && setForms(_.data().conf)
    })
  }, [urlParams])

  useEffect(() => {
    if(!forms) return
    onSnapshot(doc(collection(db, 'individuals'), urlParams.individualId), (_: DocumentSnapshot) => {
      const defaultValues = forms.reduce((a: any, b: any) => {
        a[b.name] = ""
        return a
      }, {})
      if(!_.exists()) return navigate('/noData')
      const data = _.data()
      const mergedValues = { ...defaultValues, ...data }
      setFormValues(mergedValues)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forms])

  const onChange = ({name, value}) => {
    setFormValues({...formValues, [name]: value})
  }
  const update = async () => {
    const errors = forms.map((form: any) => {
      return validate(formValues[form.name], form.label, form.validation)
    })
    const errorMessages = errors.filter((e: any) => !isEmpty(e)).join("<br/>")
    if (!isEmpty(errorMessages)) {
      setError(errorMessages)
      return
    }
    setProcessing(true)
    const updateValue = { ...formValues, updatedAt: dayjs().format('YYYY/MM/DD HH:mm') }
    if (formValues.acceptability === "可" && isEmpty(formValues.individualNo)) {
      const { data } = await createIndividualNo({}) as any
      updateValue["individualNo"] = data.individualNo
      // 預かり票リンクの送信
      await sendMail({
        to: formValues.email,
        subject: "[りんどう] 個体預かり票を発行しました",
        type: "text",
        contents: mailTemplate(urlParams.individualId || "")
      })
    }
    updateDoc(doc(collection(db, 'individuals'), urlParams.individualId), setIndividualStatus(updateValue))
      .then(async () => {
        toast.success("個体情報を更新しました")
        setError("")
      }).catch(e => {
        toast.error(e)
      }).finally(() => {
        setProcessing(false)
      })
  }

  const barcodePrint = async () => {
    printBarcode(formValues.individualNo)
  }

  const convertToCSV = (objArray) => {
    const array = typeof objArray !== "object" ? JSON.parse(objArray) : objArray;

    const str =
      `${Object.keys(array[0])
        .map((value) => `"${value}"`)
        .join(",")}` + "\r\n";

    return array.reduce((str: string, next: { [s: string]: unknown } | ArrayLike<unknown>) => {
      str +=
        `${Object.values(next)
          .map((value) => `"${value}"`)
          .join(",")}` + "\r\n";
      return str;
    }, str);
  };

  const downloadCsv = async () => {
    const link = document.createElement('a')
    if (!link) return

    const values = Object.entries(formValues).map(([k, v]) => {
      const form = forms.find((f: any) => f.name == k)
      return form && { name: form.label, value: v }
    }).filter(_ => (_ && (typeof _.value !== 'object'))).reduce((a, b) => {
      a[b.name] = b.value
      return a
    }, {})
    console.log(values)

    const csv = convertToCSV([values])
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `個体情報（${formValues.individualNo}）.csv`)
    link.click()
  }

  const isAccepted = (formValues: any) => {
    return formValues && formValues.acceptability == "可" && !isEmpty(formValues.individualNo)
  }

  const getValue = (name: string) => {
    return formValues[name]
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Box
        m={2}
        width="80%"
        height="80vh"
        display="flex-col"
        mt={5}
      >
        <TableContainer component={Paper}>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="left">項目</TableCell>
                <TableCell width="20%" align="left">入力内容</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {
                forms && formValues && forms.map((form: any) => (
                  <TableRow
                    key={form.name}
                    className={!form.isHunterElements ? "bg-yellow-200" : ""}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {form.label}{form.type === 'checkbox' ? ` (${form.title})` : ""}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {
                        form.type === "upload"
                          ? <PopImageView imageData={getValue(form.name)} width={100} />
                          : <FormElement
                              type={form.type}
                              name={form.name}
                              value={getValue(form.name)}
                              onChange={onChange}
                              options={form.options}
                              text={form.text}
                              // disabled={formDisabled}
                            />
                      }
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </TableContainer>
        <Box mt={2} display="flex" justifyContent="center">
          <Typography variant="body2" color="red">{parse(error)}</Typography>
        </Box>
        <Box mt={2} display="flex" justifyContent="center">
          <Stack direction="row" spacing={3}>
            { isAccepted(formValues) && <Fab variant="extended" color="secondary" onClick={downloadCsv} style={{ right: 20, bottom: 260, position: "fixed" }}>CSVﾀﾞｳﾝﾛｰﾄﾞ</Fab>}
            { isAccepted(formValues) && <Fab variant="extended" color="warning" onClick={barcodePrint} style={{ right: 20, bottom: 200, position: "fixed" }}>ﾊﾞｰｺｰﾄﾞ 印刷</Fab>}
            { isAccepted(formValues) && <Fab variant="extended" color="success" onClick={() => setOpenStatusModal(true)} style={{ right: 20, bottom: 140, position: "fixed" }}>個体状態登録</Fab>}
            { isAccepted(formValues) && <Fab variant="extended" color="error" onClick={() => setOpenProcessingModal(true)} style={{ right: 20, bottom: 80, position: "fixed" }}>加工記録入力</Fab>}
            <Fab variant="extended" color="info" onClick={update} style={{ right: 20, bottom: 20, position: "fixed" }}>&emsp;更&emsp;&emsp;新&emsp;</Fab>
          </Stack>
        </Box>
      </Box>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={prosessing}
        onClick={() => setProcessing(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <IndividualStatusModal isOpen={openStatusModal} individualId={urlParams.individualId || ''} onClose={() => setOpenStatusModal(false)}/>
      <ProcessingRecordModal isOpen={openProcessingModal} individualId={urlParams.individualId || ''} onClose={() => setOpenProcessingModal(false)}/>
    </Box>
  )
}
export default IndividualDetails
